/* global axios */
import ApiClient from '../ApiClient';

// Auditoria 2026-08-08 (G1): o backend expõe 12 ações de atividades do CRM
// (`Api::V1::Accounts::Pipeline::ActivitiesController`) e o dashboard não tinha
// NENHUMA camada de API para elas — nenhum arquivo chamava
// `/pipeline/activities`. Ligações, reuniões e tarefas eram criadas só por
// automações e sequências; pela interface, não havia como.
//
// Este arquivo é o primeiro passo do G1 e cobre a superfície inteira do
// controller. A tela (lista, criação, agenda) vem no ciclo próprio do item —
// ver `Roadmap/internal/chatwoot/fase-61-pipeline-auditoria-correcao.md`.
//
// ⚠️ Os lembretes (`PipelineActivityReminder`) dependem de D6c/D6d: hoje o
// canal in-app ainda não produz e o e-mail espera o outbox. Não prometer envio
// de lembrete na UI antes disso.
// O servidor EXIGE `pipeline_card_id` em toda transição (`set_pipeline_card`).
// Cross-review Codex: aceitá-lo como opcional deixava uma chamada no formato
// antigo serializar sem ele e receber um 404 enganoso — "atividade não
// encontrada" para uma atividade que existe. Falhar aqui é mais honesto.
const requireCardId = (pipelineCardId, action) => {
  if (pipelineCardId === undefined || pipelineCardId === null) {
    throw new TypeError(`pipelineCardId is required to ${action} an activity`);
  }
  return pipelineCardId;
};

class PipelineActivitiesAPI extends ApiClient {
  constructor() {
    super('pipeline/activities', { accountScoped: true });
  }

  list({ pipelineCardId, status, activityType, dateFrom, dateTo, page } = {}) {
    const params = {};
    if (pipelineCardId !== undefined) params.pipeline_card_id = pipelineCardId;
    if (status !== undefined) params.status = status;
    // ⚠️ O filtro de tipo do índice é `params[:type]` (`apply_filters` no
    // controller), NÃO `activity_type` — este último só existe na ação
    // `templates`. Enviar `activity_type` aqui era um filtro inerte: a API
    // devolvia a lista inteira e ninguém percebia. `activityType` fica no nome
    // da opção (camelCase, como o resto da camada) e vira `type` na query.
    if (activityType !== undefined) params.type = activityType;
    if (dateFrom !== undefined) params.date_from = dateFrom;
    if (dateTo !== undefined) params.date_to = dateTo;
    if (page !== undefined) params.page = page;

    return axios.get(this.url, { params });
  }

  // Transições de estado. Cada uma é um POST próprio no backend porque carrega
  // efeito colateral distinto (o `complete` fecha, o `reschedule` remarca).
  //
  // ⚠️ `pipeline_card_id` NÃO é opcional aqui, apesar de a atividade já estar
  // identificada na URL: o controller roda `set_pipeline_card` também nas
  // transições (`before_action ... except: index/search/analytics/templates/
  // timeline/bulk_create`) e resolve o card por `params[:pipeline_card_id]`.
  // Sem ele o `find(nil)` levanta RecordNotFound e a transição responde 404 —
  // com a tela mostrando "não foi possível atualizar" para uma atividade que
  // existe. Descoberto ao ligar a aba (G1); a primeira versão desta camada
  // omitia o campo.
  start(id, { pipelineCardId } = {}) {
    return axios.post(`${this.url}/${id}/start`, {
      pipeline_card_id: requireCardId(pipelineCardId, 'start'),
    });
  }

  // `outcome_notes` é o nome que o controller permite (`params.permit(:outcome,
  // :outcome_notes, …)`); enviar `notes` era escrever num campo que ninguém lia.
  complete(id, { outcome, outcomeNotes, pipelineCardId } = {}) {
    return axios.post(`${this.url}/${id}/complete`, {
      pipeline_card_id: requireCardId(pipelineCardId, 'complete'),
      outcome,
      outcome_notes: outcomeNotes,
    });
  }

  cancel(id, { reason, pipelineCardId } = {}) {
    return axios.post(`${this.url}/${id}/cancel`, {
      pipeline_card_id: requireCardId(pipelineCardId, 'cancel'),
      reason,
    });
  }

  reschedule(id, { scheduledAt, reason, pipelineCardId } = {}) {
    return axios.post(`${this.url}/${id}/reschedule`, {
      pipeline_card_id: requireCardId(pipelineCardId, 'reschedule'),
      scheduled_at: scheduledAt,
      reason,
    });
  }

  search(query, { pipelineCardId, status } = {}) {
    const params = { q: query };
    if (pipelineCardId !== undefined) params.pipeline_card_id = pipelineCardId;
    if (status !== undefined) params.status = status;

    return axios.get(`${this.url}/search`, { params });
  }

  analytics({ dateFrom, dateTo } = {}) {
    const params = {};
    if (dateFrom !== undefined) params.date_from = dateFrom;
    if (dateTo !== undefined) params.date_to = dateTo;

    return axios.get(`${this.url}/analytics`, { params });
  }

  templates() {
    return axios.get(`${this.url}/templates`);
  }

  bulkCreate(activities) {
    return axios.post(`${this.url}/bulk_create`, { activities });
  }

  createFromTemplate(templateId, { pipelineCardId, scheduledAt } = {}) {
    return axios.post(`${this.url}/create_from_template`, {
      template_id: templateId,
      pipeline_card_id: pipelineCardId,
      scheduled_at: scheduledAt,
    });
  }
}

export default new PipelineActivitiesAPI();
